import { Volume2, GraduationCap } from "lucide-react";
import { speak } from "@/lib/tts";
import { PHONIC_GUIDE } from "@/lib/phonicGuideData";

// Advanced-track phonic sound guide — one row per letter with a mnemonic
// word and phonetic tag. Tap a row to hear the sound demonstrated.
export default function PhonicGuide() {
  const play = (item) => {
    // Say the letter, then the mnemonic twice so the phoneme is clearly heard
    // at the start of a natural word. TTS can't reliably speak isolated
    // phonemes like /kw/ or /ks/ — mnemonic words solve that.
    speak(`${item.letter}. As in ${item.mnemonic}. ${item.mnemonic}.`, { rate: 0.8 });
  };

  return (
    <section
      id="phonic-guide"
      data-testid="phonic-guide-section"
      className="mt-16"
    >
      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest bg-amber-50 text-amber-700 px-3 py-1 rounded-full mb-3">
        <GraduationCap className="w-3.5 h-3.5" /> Adult phonic sound guide
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
        The sound each letter makes
      </h2>
      <p className="mt-3 text-slate-600 text-lg max-w-2xl">
        A quick pronunciation reference tuned for teens and adults. Each letter
        pairs with a short mnemonic word (e.g. <span className="font-bold text-amber-600">H → Her → /h/</span>) so
        the phoneme leads the word.
      </p>

      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {PHONIC_GUIDE.map((item) => (
          <button
            key={item.letter}
            data-testid={`phonic-guide-${item.letter}`}
            onClick={() => play(item)}
            aria-label={`Play the sound of letter ${item.letter} as in ${item.mnemonic}`}
            className="group text-left flex items-center gap-4 p-4 bg-white rounded-2xl border-2 border-slate-100 hover:border-amber-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-4 focus:ring-amber-100"
          >
            <div className="font-display text-4xl font-semibold text-amber-600 w-10 leading-none">
              {item.letter}
            </div>
            <div className="flex-1">
              <div className="font-display text-xl font-medium text-slate-800">
                {item.mnemonic}
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-0.5">
                /{item.sound}/
              </div>
            </div>
            <Volume2 className="w-5 h-5 text-slate-400 group-hover:text-amber-500 transition-colors" />
          </button>
        ))}
      </div>
    </section>
  );
}
